import React, { useState } from 'react';

const CaseStudiesSection: React.FC = () => {
  const [activeCase, setActiveCase] = useState(0);

  const caseStudies = [
    {
      title: 'E-commerce Analytics Platform',
      client: 'RetailTech Solutions',
      category: 'Data Engineering',
      challenge: 'Complex data integration from multiple sources including inventory, sales, and customer behavior data.',
      solution: 'Built a comprehensive data pipeline using modern ETL tools and created real-time dashboards for business intelligence.',
      results: [
        '40% improvement in data processing speed',
        '60% reduction in manual reporting time',
        'Real-time inventory tracking across 50+ stores',
        '$2M+ in cost savings through optimized operations'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20e-commerce%20analytics%20dashboard%20displaying%20colorful%20charts%2C%20graphs%2C%20and%20data%20visualizations%20on%20multiple%20screens%20in%20a%20professional%20office%20environment%20with%20clean%20design%20and%20contemporary%20technology%20setup&width=600&height=400&seq=case-study-001&orientation=landscape',
      technologies: ['Python', 'Apache Airflow', 'PostgreSQL', 'Tableau', 'AWS']
    },
    {
      title: 'AI-Powered Customer Service',
      client: 'TechCorp Industries',
      category: 'AI Agents',
      challenge: 'High volume of customer inquiries overwhelming support team and increasing response times.',
      solution: 'Implemented intelligent AI agents with natural language processing to handle routine inquiries and escalate complex issues.',
      results: [
        '70% reduction in response time',
        '85% of inquiries handled automatically',
        '95% customer satisfaction rate',
        '50% reduction in support costs'
      ],
      image: 'https://readdy.ai/api/search-image?query=Futuristic%20AI%20customer%20service%20interface%20with%20chatbot%20conversations%2C%20natural%20language%20processing%20visualizations%2C%20and%20modern%20customer%20support%20dashboard%20in%20a%20sleek%20technology%20environment%20with%20holographic%20elements&width=600&height=400&seq=case-study-002&orientation=landscape',
      technologies: ['LangChain', 'OpenAI GPT', 'React', 'Node.js', 'MongoDB']
    },
    {
      title: 'Healthcare Monitoring System',
      client: 'MedTech Innovations',
      category: 'Observability',
      challenge: 'Critical need for real-time monitoring of medical equipment and patient data across multiple facilities.',
      solution: 'Developed comprehensive observability stack with predictive analytics and automated alerting systems.',
      results: [
        '99.9% system uptime achieved',
        '30% faster incident response',
        'Zero critical system failures',
        'Improved patient safety metrics'
      ],
      image: 'https://readdy.ai/api/search-image?query=Advanced%20healthcare%20monitoring%20system%20with%20medical%20equipment%20displays%2C%20real-time%20patient%20data%20visualizations%2C%20and%20modern%20hospital%20technology%20infrastructure%20in%20a%20clean%20clinical%20environment&width=600&height=400&seq=case-study-003&orientation=landscape',
      technologies: ['Prometheus', 'Grafana', 'Kubernetes', 'Docker', 'Elasticsearch']
    },
    {
      title: 'Fintech MVP Launch',
      client: 'StartupFinance',
      category: 'MVP Development',
      challenge: 'Startup needed to quickly validate their financial services concept with a market-ready product.',
      solution: 'Delivered a complete fintech platform in 14 days with core features, security compliance, and scalable architecture.',
      results: [
        'Launched in 14 days as promised',
        '10,000+ users in first month',
        '$500K seed funding secured',
        'Platform scaled to handle 100K+ transactions'
      ],
      image: 'https://readdy.ai/api/search-image?query=Modern%20fintech%20application%20interface%20showing%20financial%20dashboards%2C%20transaction%20flows%2C%20and%20mobile%20banking%20features%20in%20a%20sleek%20digital%20environment%20with%20professional%20design%20elements&width=600&height=400&seq=case-study-004&orientation=landscape',
      technologies: ['React Native', 'Node.js', 'PostgreSQL', 'Stripe', 'AWS Lambda']
    }
  ];

  return (
    <section id="case-studies" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Case Studies
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Real results from real projects. See how we've helped businesses 
            transform and achieve their goals.
          </p>
          <div className="mt-8 bg-teal-500 w-24 h-1 mx-auto rounded-full"></div>
        </div>

        {/* Case Study Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {caseStudies.map((study, index) => (
            <button
              key={index}
              onClick={() => setActiveCase(index)}
              className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCase === index
                  ? 'bg-teal-500 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {study.category}
            </button>
          ))}
        </div>

        {/* Active Case Study */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative h-64 lg:h-auto">
              <img 
                src={caseStudies[activeCase].image}
                alt={caseStudies[activeCase].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="p-8 lg:p-12">
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-teal-100 text-teal-600 text-sm font-medium rounded-full mb-4">
                  {caseStudies[activeCase].category}
                </span>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">
                  {caseStudies[activeCase].title}
                </h3>
                <p className="text-lg text-gray-600">
                  Client: {caseStudies[activeCase].client}
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Challenge</h4>
                  <p className="text-gray-600">{caseStudies[activeCase].challenge}</p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Solution</h4>
                  <p className="text-gray-600">{caseStudies[activeCase].solution}</p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Results</h4>
                  <ul className="space-y-2">
                    {caseStudies[activeCase].results.map((result, index) => (
                      <li key={index} className="flex items-start">
                        <i className="ri-check-line text-teal-500 text-lg mr-3 mt-0.5"></i>
                        <span className="text-gray-600">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Technologies Used</h4>
                  <div className="flex flex-wrap gap-2">
                    {caseStudies[activeCase].technologies.map((tech, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center mt-8 space-x-2">
          {caseStudies.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveCase(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                activeCase === index ? 'bg-teal-500' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;